/**
 * canvasUtils — shared 2D-canvas helpers used across the Green Screen
 * pipeline (live compositor, mobile chip previews, VOD tool, chroma-key
 * mixers). Consolidates helpers that were duplicated across
 * `useGreenScreen.js`, `useFxCompositor.js`, `GreenScreenStudio.jsx`,
 * `StudioFxModal.jsx`, and `MobileGreenScreenBar.jsx`.
 *
 * All functions here are pure — no React state, no side-effects.
 */

/**
 * Draw `src` (HTMLVideoElement, HTMLImageElement, or another canvas)
 * onto `ctx` covering a `w`×`h` region with a centre-crop fill (never
 * letterboxes). This is the same behaviour as CSS `object-fit: cover`.
 */
export function drawCover(ctx, src, w, h) {
  const srcW = src?.videoWidth || src?.naturalWidth || src?.width || w;
  const srcH = src?.videoHeight || src?.naturalHeight || src?.height || h;
  if (!srcW || !srcH) return;
  const srcAR = srcW / srcH;
  const dstAR = w / h;
  let dw = w, dh = h, dx = 0, dy = 0;
  if (srcAR > dstAR) {
    dw = h * srcAR;
    dx = (w - dw) / 2;
  } else {
    dh = w / srcAR;
    dy = (h - dh) / 2;
  }
  try { ctx.drawImage(src, dx, dy, dw, dh); } catch { /* stream not ready */ }
}

/**
 * Parse a `#RRGGBB` hex colour into an `{r, g, b}` tuple with each
 * channel in 0..255. Accepts falsy input and returns pure green
 * (`#00FF00`, the standard chroma-key colour).
 */
export function hexToRgb(hex) {
  const h = (hex || "#00FF00").replace("#", "");
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
